import { LoadMore } from "../../components/LoadMore";
import { Pagination } from "../../components/Pagination";
import { cn } from "../../lib/utils";
import { flexRender, functionalUpdate, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { breakpoints, useWindowSize } from "../WindowUtility";
import { Cell } from "./cells";
import { DataTableLayoutContext } from "./layout";
import { Body, Head, Header, Root, Row } from "./parts";
import { ALIGN_JUSTIFY_CLASS, getDataTableColumnPresets } from "./presets";
export function shouldRenderColumn(windowWidth, breakpoint) {
  if (!breakpoint) {
    return true;
  }
  return windowWidth >= breakpoints[breakpoint];
}
const ROW_REVEAL_DURATION_MS = 300;

// Animates the table height (not the footer) so appended rows slide in at the
// rate the footer moves down; animating the footer lets rows pop in ahead of it.
function useRowRevealAnimation(rowCount, enabled) {
  const ref = useRef(null);
  const previousHeightRef = useRef(null);
  const previousRowCountRef = useRef(rowCount);

  // Cells are skipped on the first commit (window size not measured yet), so a
  // mount-only height would be ~0. ResizeObserver runs after layout effects, so
  // the stored value is always the height from before the new rows landed.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || !enabled) {
      return;
    }
    previousHeightRef.current = element.scrollHeight;
    const observer = new ResizeObserver(() => {
      previousHeightRef.current = element.scrollHeight;
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled]);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    // scrollHeight stays the content height while `height` is pinned mid-animation.
    const height = element.scrollHeight;
    const previousHeight = previousHeightRef.current;
    const grew = rowCount > previousRowCountRef.current;
    previousRowCountRef.current = rowCount;
    if (!enabled || !grew || previousHeight === null || previousHeight >= height || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const clear = () => {
      element.style.transition = "";
      element.style.height = "";
      element.style.overflow = "";
    };
    element.style.overflow = "hidden";
    element.style.transition = "none";
    element.style.height = `${previousHeight}px`;
    const frame = requestAnimationFrame(() => {
      element.style.transition = `height ${ROW_REVEAL_DURATION_MS}ms ease-out`;
      element.style.height = `${height}px`;
    });
    element.addEventListener("transitionend", clear, {
      once: true
    });
    return () => {
      cancelAnimationFrame(frame);
      element.removeEventListener("transitionend", clear);
      clear();
    };
  }, [rowCount, enabled]);
  return ref;
}

// Adapts a controlled `(value) => void` setter to TanStack's Updater API; only when both the value and its setter are provided.
function useControlledUpdater(value, setValue) {
  return value && setValue ? updater => {
    setValue(functionalUpdate(updater, value));
  } : undefined;
}
// Builds the TanStack table for DataTableBase, wiring controlled state and the text filter.
function useDataTableInstance({
  data,
  columns,
  totalRowCount,
  filter,
  filterColumn,
  pagination,
  setPagination,
  sorting,
  setSorting,
  isServerSideSorting,
  rowSelection,
  setRowSelection,
  enableRowSelection,
  enableMultiRowSelection,
  getRowId,
  enableSortingRemoval,
  getRowLabel
}) {
  const [columnFilters, setColumnFilters] = useState([]);
  const isServerSidePagination = !!totalRowCount && totalRowCount > data.length;
  const isClientSideSortingEnabled = !isServerSideSorting && !isServerSidePagination;
  const onPaginationChange = useControlledUpdater(pagination, setPagination);
  const onSortingChange = useControlledUpdater(sorting, setSorting);
  const onRowSelectionChange = useControlledUpdater(rowSelection, setRowSelection);
  const table = useReactTable({
    data,
    columns,
    rowCount: totalRowCount,
    manualPagination: isServerSidePagination,
    manualSorting: isServerSideSorting,
    ...(isServerSideSorting && {
      onSortingChange: onSortingChange
    }),
    enableSortingRemoval,
    getCoreRowModel: getCoreRowModel(),
    ...(!isServerSideSorting && {
      getSortedRowModel: getSortedRowModel(),
      enableSorting: isClientSideSortingEnabled
    }),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
    onColumnFiltersChange: setColumnFilters,
    ...(enableRowSelection && {
      onRowSelectionChange
    }),
    state: {
      columnFilters,
      ...(isServerSideSorting && {
        sorting
      }),
      pagination,
      ...(enableRowSelection && {
        rowSelection
      })
    },
    initialState: {
      sorting
    },
    onPaginationChange,
    enableRowSelection,
    enableMultiRowSelection,
    getRowId,
    ...(getRowLabel && {
      meta: {
        getRowLabel
      }
    })
  });
  useEffect(() => {
    if (filterColumn) {
      var _table$getColumn;
      (_table$getColumn = table.getColumn(filterColumn)) === null || _table$getColumn === void 0 || _table$getColumn.setFilterValue(filter);
    }
    // oxlint-disable-next-line react/exhaustive-deps -- not reported by the previous linter; deps kept as-is
  }, [filter, filterColumn]);
  return table;
}
function DataTableHeaderRow({
  headerGroup,
  widthClassName,
  isColumnVisible
}) {
  return /*#__PURE__*/React.createElement(Row, {
    widthClassName: widthClassName
  }, headerGroup.headers.map(header => {
    if (!isColumnVisible(header.id)) {
      return null;
    }
    const canSort = header.column.getCanSort() && getDataTableColumnPresets(header.column).sortable;
    return /*#__PURE__*/React.createElement(Head, {
      column: header.column,
      key: header.id,
      onSort: canSort ? header.column.getToggleSortingHandler() : undefined
    }, renderHeaderContent(header, canSort));
  }));
}
function DataTableBodyRow({
  row,
  widthClassName,
  hideRowDivider,
  enableRowSelection,
  enableMultiRowSelection,
  disableRowClickSelection,
  isColumnVisible
}) {
  const handleRowClick = () => {
    var _row$original$onClick, _row$original;
    if (enableRowSelection && row.getCanSelect()) {
      row.toggleSelected(!enableMultiRowSelection ? true : undefined);
    }
    (_row$original$onClick = (_row$original = row.original).onClick) === null || _row$original$onClick === void 0 || _row$original$onClick.call(_row$original);
  };
  return /*#__PURE__*/React.createElement(Row, {
    widthClassName: widthClassName,
    hideBottomBorder: hideRowDivider,
    onClick: enableRowSelection && !disableRowClickSelection ? handleRowClick : row.original.onClick,
    onDoubleClick: row.original.onDoubleClick,
    rowData: row.original,
    ...(enableRowSelection && {
      "data-selected": row.getIsSelected()
    })
  }, row.getVisibleCells().map(cell => {
    if (!isColumnVisible(cell.column.id)) {
      return null;
    }
    return /*#__PURE__*/React.createElement(Cell, {
      column: cell.column,
      key: cell.id
    }, flexRender(cell.column.columnDef.cell, cell.getContext()));
  }));
}
// Pagination when controlled pagination is set, otherwise the "Load more" control, otherwise nothing.
function DataTableFooter({
  table,
  pagination,
  rowCount,
  totalRowCount,
  rowCountIsCapped,
  disablePaginationNumbers,
  onLoadMore,
  isLoadingMore,
  onShowLess
}) {
  if (pagination) {
    return /*#__PURE__*/React.createElement("div", {
      className: "p-1"
    }, /*#__PURE__*/React.createElement(Pagination, {
      size: "xs",
      pagination: table.getState().pagination,
      setPagination: table.setPagination,
      rowCount: table.getRowCount(),
      rowCountIsCapped: rowCountIsCapped,
      disablePaginationNumbers: disablePaginationNumbers
    }));
  }
  if (onLoadMore) {
    return /*#__PURE__*/React.createElement("div", {
      className: "p-1"
    }, /*#__PURE__*/React.createElement(LoadMore, {
      onLoadMore: onLoadMore,
      onShowLess: onShowLess,
      isLoading: isLoadingMore,
      rowCount: rowCount,
      totalRowCount: totalRowCount,
      totalRowCountIsCapped: rowCountIsCapped
    }));
  }
  return null;
}

/**
 * @cc [owner:aubin-tchoi,label:product;react] minimal-columns
 * `columns` MUST contain only the columns the table's purpose requires; a new column MUST NOT be
 * added when its data fits in an existing column (e.g. as a secondary line or description) or
 * belongs in a details view.
 */
/**
 * @cc [owner:aubin-tchoi,label:product;react] header-never-clipped
 * In every supported locale (en-US, en-GB, fr-FR, de-DE), each rendered header label and its sort
 * icon MUST be fully visible, without truncation or overflow, at every viewport width where its
 * column is rendered per `columnsBreakpoints`.
 */
/**
 * Sortable, filterable, paginated data table built on TanStack Table, with row
 * selection and a "Load more" footer. For very long or infinite datasets prefer
 * ScrollableDataTable, which virtualizes rows.
 * @summary Sortable, filterable, paginated data table.
 */
export function DataTableBase({
  data,
  totalRowCount,
  rowCountIsCapped = false,
  columns,
  className,
  widthClassName = "w-full",
  filter,
  filterColumn,
  columnsBreakpoints = {},
  pagination,
  setPagination,
  onLoadMore,
  isLoadingMore = false,
  onShowLess,
  sorting,
  setSorting,
  isServerSideSorting = false,
  disablePaginationNumbers = false,
  rowSelection,
  setRowSelection,
  enableRowSelection = false,
  enableMultiRowSelection = true,
  getRowId,
  enableSortingRemoval = true,
  hideRowDivider = false,
  disableRowClickSelection = false,
  density = "default",
  isLoading = false,
  emptyState,
  getRowLabel,
  stickyHeader = false,
  maxHeight,
  horizontalScroll = false
}) {
  const windowSize = useWindowSize();
  const table = useDataTableInstance({
    data,
    columns,
    totalRowCount,
    filter,
    filterColumn,
    pagination,
    setPagination,
    sorting,
    setSorting,
    isServerSideSorting,
    rowSelection,
    setRowSelection,
    enableRowSelection,
    enableMultiRowSelection,
    getRowId,
    enableSortingRemoval,
    getRowLabel
  });
  const rows = table.getRowModel().rows;
  // Rendered row count, not data.length, so filtering keeps the height in sync.
  const rowRevealRef = useRowRevealAnimation(rows.length, !!onLoadMore && !pagination);
  const isColumnVisible = columnId => !!windowSize.width && shouldRenderColumn(windowSize.width, columnsBreakpoints[columnId]);
  const visibleColumnCount = table.getVisibleLeafColumns().filter(column => isColumnVisible(column.id)).length;
  const layout = useMemo(() => ({
    density,
    enforceColumnMinWidth: horizontalScroll
  }), [density, horizontalScroll]);
  return /*#__PURE__*/React.createElement(DataTableLayoutContext.Provider, {
    value: layout
  }, /*#__PURE__*/React.createElement("div", {
    className: cn("flex flex-col gap-2", className, widthClassName)
  }, /*#__PURE__*/React.createElement(Root, {
    containerRef: rowRevealRef,
    containerClassName: cn(horizontalScroll && "overflow-x-auto", maxHeight && "overflow-y-auto", maxHeight)
    // table-auto lets the table outgrow its container; fixed layout never overflows.
    ,
    className: cn(horizontalScroll && "w-max min-w-full table-auto")
  }, /*#__PURE__*/React.createElement(Header, {
    className: cn(
    // Borders do not stick with border-collapse, so the divider is a shadow.
    stickyHeader && "sticky top-0 z-20 bg-background shadow-[inset_0_-1px_0_var(--color-separator)]")
  }, table.getHeaderGroups().map(headerGroup => /*#__PURE__*/React.createElement(DataTableHeaderRow, {
    key: headerGroup.id,
    headerGroup: headerGroup,
    widthClassName: widthClassName,
    isColumnVisible: isColumnVisible
  }))), /*#__PURE__*/React.createElement(Body, {
    "aria-busy": isLoading || undefined,
    className: cn(isLoading && "pointer-events-none opacity-50 transition-opacity duration-enter ease-enter motion-reduce:transition-none")
  }, rows.map(row => /*#__PURE__*/React.createElement(DataTableBodyRow, {
    key: row.id,
    row: row,
    widthClassName: widthClassName,
    hideRowDivider: hideRowDivider,
    enableRowSelection: enableRowSelection,
    enableMultiRowSelection: enableMultiRowSelection,
    disableRowClickSelection: disableRowClickSelection,
    isColumnVisible: isColumnVisible
  })), rows.length === 0 && emptyState !== undefined && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    colSpan: Math.max(visibleColumnCount, 1),
    className: "px-2 py-8 text-center text-sm text-muted-foreground"
  }, emptyState)))), /*#__PURE__*/React.createElement(DataTableFooter, {
    table: table,
    pagination: pagination,
    rowCount: data.length,
    totalRowCount: totalRowCount,
    rowCountIsCapped: rowCountIsCapped,
    disablePaginationNumbers: disablePaginationNumbers,
    onLoadMore: onLoadMore,
    isLoadingMore: isLoadingMore,
    onShowLess: onShowLess
  })));
}

/** Sortable headers render straight into Head's sort button; others keep the flex wrapper so existing layouts do not move. */
export function renderHeaderContent(header, canSort) {
  const content = flexRender(header.column.columnDef.header, header.getContext());
  if (canSort) {
    return content;
  }
  const {
    headerAlign
  } = getDataTableColumnPresets(header.column);
  return /*#__PURE__*/React.createElement("div", {
    className: cn(
    // Clip on the wrapper rather than in a span so headers that size
    // themselves (e.g. a centered select-all checkbox) still fill the cell.
    "flex min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap", headerAlign !== "left" && ALIGN_JUSTIFY_CLASS[headerAlign])
  }, content);
}
//# sourceMappingURL=DataTable.js.map